import type { QueryClient, QueryKey } from "@tanstack/react-query"

import { queryKeys } from "./query-keys"

type Invalidation = {
  exact?: boolean
  queryKey: QueryKey
  refetchType?: `active` | `all` | `inactive` | `none`
}

const invalidate = (
  queryClient: QueryClient,
  invalidations: readonly Invalidation[],
): Promise<void[]> =>
  Promise.all(
    invalidations.map(({ exact, queryKey, refetchType }) =>
      queryClient.invalidateQueries({ exact, queryKey, refetchType }),
    ),
  )

const enfinTpoPricingInvalidation = (proposalId: string): Invalidation => ({
  queryKey: queryKeys.enfin.tpoPricings(proposalId),
  refetchType: `active`,
})

const moduleInverterCompatibilityInvalidation = (
  proposalId: string,
): Invalidation => ({
  queryKey: queryKeys.equipment.moduleInverterCompatibility(proposalId),
  refetchType: `active`,
})

type ProposalPricingInputsChangedOptions = {
  /**
   * When true, skip actively refetching REST savings. Used after a utility
   * selection that queued a background recalculation so a still-stale snapshot
   * is not presented as the completed result.
   */
  deferSavingsRefetch?: boolean
}

const proposalPricingInputInvalidations = (
  proposalId: string,
  { deferSavingsRefetch = false }: ProposalPricingInputsChangedOptions = {},
): Invalidation[] => [
  // Exact match when deferring so `['proposal', id]` does not cascade into the
  // still-stale `.../savings` query and actively refetch it as "complete".
  deferSavingsRefetch
    ? { queryKey: queryKeys.proposal.byId(proposalId), exact: true }
    : { queryKey: queryKeys.proposal.byId(proposalId) },
  ...(deferSavingsRefetch
    ? [
        { queryKey: queryKeys.proposal.details(proposalId) },
        { queryKey: queryKeys.proposal.equipmentPreviews(proposalId) },
      ]
    : []),
  {
    queryKey: queryKeys.proposal.pricing(proposalId),
    refetchType: `active`,
  },
  {
    queryKey: queryKeys.proposal.pricingComparisons(proposalId),
    refetchType: `active`,
  },
  {
    queryKey: queryKeys.concert.pricing(proposalId),
    refetchType: `active`,
  },
  enfinTpoPricingInvalidation(proposalId),
  {
    queryKey: queryKeys.lightReach.byProposal(proposalId),
    refetchType: `active`,
  },
  moduleInverterCompatibilityInvalidation(proposalId),
  ...(deferSavingsRefetch
    ? []
    : [
        {
          queryKey: queryKeys.proposal.savings(proposalId),
          refetchType: `active` as const,
        },
      ]),
]

/**
 * The pricing-input steps a design change can move: adder quantities and the
 * multiple-arrays adder follow the layout, rebates the active design, and
 * Protection Plans, incentives and VPP programs its equipment.
 */
/**
 * The LightReach read and the Summary: a new design makes LightReach's offers
 * stale and its production prerequisite out of date.
 */
const solarLightReachReaders = (proposalId: string) => [
  queryKeys.solarProposal.lightReach(proposalId),
  queryKeys.solarProposal.summary(proposalId),
]

const solarDesignPricingInputSteps = (proposalId: string) => [
  queryKeys.solarProposal.adders(proposalId),
  queryKeys.solarProposal.rebates(proposalId),
  queryKeys.solarProposal.extendedWarranties(proposalId),
  queryKeys.solarProposal.incentives(proposalId),
  queryKeys.solarProposal.vppPrograms(proposalId),
]

export const invalidationIntents = {
  auroraDesignRefreshed: (
    queryClient: QueryClient,
    proposalId: string,
  ): Promise<void[]> =>
    invalidate(queryClient, [
      { queryKey: queryKeys.proposalDesign.active(proposalId) },
      { queryKey: queryKeys.proposalDesign.activeAurora(proposalId) },
      { queryKey: queryKeys.proposalDesign.auroraProjectDesigns(proposalId) },
      { queryKey: queryKeys.design.all },
      { queryKey: queryKeys.proposal.byId(proposalId) },
      { queryKey: queryKeys.proposalLoanPrograms.byProposal(proposalId) },
      {
        queryKey: queryKeys.concert.pricing(proposalId),
        refetchType: `active`,
      },
      enfinTpoPricingInvalidation(proposalId),
      {
        queryKey: queryKeys.proposal.pricingComparisons(proposalId),
        refetchType: `active`,
      },
      {
        queryKey: queryKeys.proposal.savings(proposalId),
        refetchType: `active`,
      },
      { queryKey: queryKeys.proposalVppIncentives.byProposal(proposalId) },
      { queryKey: queryKeys.proposalVppIncentives.eligible(proposalId) },
      {
        queryKey: queryKeys.legacy.graphql.equipmentForProposal,
        refetchType: `active`,
      },
      moduleInverterCompatibilityInvalidation(proposalId),
      {
        queryKey: queryKeys.legacy.graphql.proposal,
        refetchType: `active`,
      },
      { queryKey: queryKeys.lightReach.byProposal(proposalId) },
    ]),

  activeLoanChanged: (
    queryClient: QueryClient,
    { activeLoanId, proposalId }: { activeLoanId: string; proposalId: string },
  ): Promise<void[]> =>
    invalidate(queryClient, [
      { queryKey: queryKeys.proposal.byId(proposalId) },
      { queryKey: queryKeys.activeLoan.byId(activeLoanId) },
      { queryKey: queryKeys.activeLoan.activity(activeLoanId) },
    ]),

  completedDesignActivated: (
    queryClient: QueryClient,
    proposalId: string,
  ): Promise<void[]> =>
    invalidate(queryClient, [
      { queryKey: queryKeys.proposal.byId(proposalId) },
      { queryKey: queryKeys.design.all },
      enfinTpoPricingInvalidation(proposalId),
      // The Solar V2 versions panel shows which version is active.
      {
        queryKey: queryKeys.solarProposal.designVersions(proposalId),
        exact: true,
      },
    ]),

  enfinTpoPricingInputsChanged: (
    queryClient: QueryClient,
    proposalId: string,
  ): Promise<void[]> =>
    invalidate(queryClient, [enfinTpoPricingInvalidation(proposalId)]),

  extendedWarrantySelectionChanged: (
    queryClient: QueryClient,
    proposalId: string,
  ): Promise<void[]> =>
    invalidate(queryClient, [
      ...proposalPricingInputInvalidations(proposalId),
      {
        queryKey: queryKeys.legacy.graphql.design,
        refetchType: `active`,
      },
      {
        queryKey: queryKeys.legacy.graphql.proposal,
        refetchType: `active`,
      },
      {
        exact: true,
        queryKey: queryKeys.legacy.graphql.warrantyPricing(proposalId),
        refetchType: `active`,
      },
    ]),

  concertChangeOrderSubmitted: (
    queryClient: QueryClient,
    {
      activeLoanId,
      proposalId,
    }: { activeLoanId?: string | null; proposalId: string },
  ): Promise<void[]> =>
    invalidate(queryClient, [
      {
        queryKey: queryKeys.proposal.savings(proposalId),
        refetchType: `active`,
      },
      { queryKey: queryKeys.proposal.byId(proposalId) },
      { queryKey: queryKeys.proposal.details(proposalId) },
      {
        queryKey: queryKeys.legacy.graphql.proposal,
        refetchType: `active`,
      },
      {
        queryKey: queryKeys.legacy.graphql.quotesForProposal,
        refetchType: `active`,
      },
      { queryKey: queryKeys.concert.pricing(proposalId) },
      { queryKey: queryKeys.concert.loanDocsReadiness(proposalId) },
      ...(activeLoanId
        ? [
            { queryKey: queryKeys.activeLoan.byId(activeLoanId) },
            { queryKey: queryKeys.activeLoan.activity(activeLoanId) },
          ]
        : []),
    ]),

  enfinTpoPricingApplied: (
    queryClient: QueryClient,
    proposalId: string,
  ): Promise<void[]> =>
    invalidate(queryClient, [
      { queryKey: queryKeys.proposal.details(proposalId) },
      { queryKey: queryKeys.proposal.pricing(proposalId) },
      { queryKey: queryKeys.legacy.graphql.proposal },
      { queryKey: queryKeys.legacy.graphql.quotesForProposal },
    ]),

  lightReachApplicationSubmitted: (
    queryClient: QueryClient,
    proposalId: string,
  ): Promise<void[]> =>
    invalidate(queryClient, [
      { queryKey: queryKeys.lightReach.byProposal(proposalId) },
      { queryKey: queryKeys.proposal.byId(proposalId) },
    ]),

  proposalDesignChanged: (
    queryClient: QueryClient,
    proposalId: string,
  ): Promise<void[]> =>
    invalidate(queryClient, [
      { queryKey: queryKeys.proposalDesign.active(proposalId) },
      { queryKey: queryKeys.proposalDesign.activeAurora(proposalId) },
      { queryKey: queryKeys.proposalDesign.auroraProjectDesigns(proposalId) },
      { queryKey: queryKeys.proposal.byId(proposalId) },
      { queryKey: queryKeys.design.all },
      enfinTpoPricingInvalidation(proposalId),
    ]),

  proposalFinancePlanChanged: (
    queryClient: QueryClient,
    proposalId: string,
  ): Promise<void[]> =>
    invalidate(queryClient, [
      { queryKey: queryKeys.proposal.byId(proposalId) },
      { queryKey: queryKeys.proposalLoanPrograms.byProposal(proposalId) },
      {
        queryKey: queryKeys.concert.pricing(proposalId),
        refetchType: `active`,
      },
      {
        queryKey: queryKeys.proposal.savings(proposalId),
        refetchType: `active`,
      },
      { queryKey: queryKeys.proposalVppIncentives.byProposal(proposalId) },
      { queryKey: queryKeys.proposalVppIncentives.eligible(proposalId) },
      moduleInverterCompatibilityInvalidation(proposalId),
    ]),

  proposalFinanceProductChanged: (
    queryClient: QueryClient,
    proposalId: string,
  ): Promise<void[]> =>
    invalidate(queryClient, [
      {
        queryKey:
          queryKeys.proposal.pricingInvalidationLegacyWrapper(proposalId),
        refetchType: `active`,
      },
      { queryKey: queryKeys.lightReach.byProposal(proposalId) },
      {
        queryKey: queryKeys.concert.pricing(proposalId),
        refetchType: `active`,
      },
      moduleInverterCompatibilityInvalidation(proposalId),
      { queryKey: queryKeys.financeProduct.all },
      {
        queryKey: queryKeys.legacy.graphql.quotesForProposal,
        refetchType: `active`,
      },
      {
        queryKey: queryKeys.proposal.savings(proposalId),
        refetchType: `active`,
      },
    ]),

  proposalLoanProgramsChanged: (
    queryClient: QueryClient,
    proposalId: string,
  ): Promise<void[]> =>
    invalidate(queryClient, [
      { queryKey: queryKeys.proposalLoanPrograms.byProposal(proposalId) },
      { queryKey: queryKeys.proposal.byId(proposalId) },
      {
        queryKey: queryKeys.proposal.pricing(proposalId),
        refetchType: `none`,
      },
      {
        queryKey: queryKeys.proposal.pricingComparisons(proposalId),
        refetchType: `active`,
      },
      {
        queryKey: queryKeys.concert.pricing(proposalId),
        refetchType: `active`,
      },
      {
        queryKey: queryKeys.proposal.savings(proposalId),
        refetchType: `active`,
      },
      {
        queryKey: queryKeys.legacy.graphql.equipmentForProposal,
        refetchType: `active`,
      },
      moduleInverterCompatibilityInvalidation(proposalId),
    ]),

  proposalPricingInputsChanged: (
    queryClient: QueryClient,
    proposalId: string,
    options?: ProposalPricingInputsChangedOptions,
  ): Promise<void[]> =>
    invalidate(
      queryClient,
      proposalPricingInputInvalidations(proposalId, options),
    ),

  /**
   * A Solar Proposal V2 Aurora link, version switch or refresh: the design and the
   * reads it feeds. Pricing, savings and contract readiness refetch to show
   * they are now out of date; nothing recalculates them. Section keys are exact so `byId` doesn't
   * cascade; the Aurora and product-offering prefixes also drop the project
   * listing and the equipment previews sized for the old design.
   */
  solarAuroraDesignChanged: (
    queryClient: QueryClient,
    proposalId: string,
  ): Promise<void[]> =>
    invalidate(queryClient, [
      { queryKey: queryKeys.solarProposal.aurora(proposalId) },
      { queryKey: queryKeys.solarProposal.productOffering(proposalId) },
      // The switch replaces the design's equipment, AI compute node with it,
      // so the Compute read and its preview follow the new design.
      { queryKey: queryKeys.solarProposal.compute(proposalId) },
      ...[
        queryKeys.solarProposal.design(proposalId),
        // A first Aurora link makes the Vision versions read-only.
        queryKeys.solarProposal.designVersions(proposalId),
        queryKeys.solarProposal.designer(proposalId),
        queryKeys.solarProposal.equipment(proposalId),
        queryKeys.solarProposal.impact(proposalId),
        queryKeys.solarProposal.savings(proposalId),
        queryKeys.solarProposal.pricing(proposalId),
        queryKeys.solarProposal.systemPricing(proposalId),
        queryKeys.solarProposal.share(proposalId),
        queryKeys.solarProposal.byId(proposalId),
        queryKeys.solarProposal.contract(proposalId),
        ...solarDesignPricingInputSteps(proposalId),
        ...solarLightReachReaders(proposalId),
      ].map((queryKey) => ({ queryKey, exact: true })),
    ]),

  /**
   * A Vision design version switch or duplicate (VIS-839): the active design,
   * its equipment and production change, so every read it feeds refetches;
   * pricing, savings and the share link then read out of date until someone
   * refreshes them on purpose.
   */
  solarDesignVersionChanged: (
    queryClient: QueryClient,
    proposalId: string,
  ): Promise<void[]> =>
    invalidate(queryClient, [
      { queryKey: queryKeys.solarProposal.productOffering(proposalId) },
      { queryKey: queryKeys.solarProposal.compute(proposalId) },
      ...[
        queryKeys.solarProposal.design(proposalId),
        queryKeys.solarProposal.designVersions(proposalId),
        queryKeys.solarProposal.designer(proposalId),
        queryKeys.solarProposal.equipment(proposalId),
        queryKeys.solarProposal.impact(proposalId),
        queryKeys.solarProposal.savings(proposalId),
        queryKeys.solarProposal.pricing(proposalId),
        queryKeys.solarProposal.systemPricing(proposalId),
        queryKeys.solarProposal.share(proposalId),
        queryKeys.solarProposal.byId(proposalId),
        queryKeys.solarProposal.contract(proposalId),
        ...solarDesignPricingInputSteps(proposalId),
        ...solarLightReachReaders(proposalId),
      ].map((queryKey) => ({ queryKey, exact: true })),
    ]),

  proposalProductOfferingChanged: (
    queryClient: QueryClient,
    proposalId: string,
  ): Promise<void[]> =>
    invalidate(queryClient, [
      { queryKey: queryKeys.design.all },
      { queryKey: queryKeys.proposal.byId(proposalId) },
      { queryKey: queryKeys.proposalLoanPrograms.byProposal(proposalId) },
      {
        queryKey: queryKeys.concert.pricing(proposalId),
        refetchType: `active`,
      },
      enfinTpoPricingInvalidation(proposalId),
      {
        queryKey: queryKeys.proposal.pricingComparisons(proposalId),
        refetchType: `active`,
      },
      {
        queryKey: queryKeys.proposal.savings(proposalId),
        refetchType: `active`,
      },
      { queryKey: queryKeys.proposalVppIncentives.byProposal(proposalId) },
      { queryKey: queryKeys.proposalVppIncentives.eligible(proposalId) },
      {
        queryKey: queryKeys.legacy.graphql.equipmentForProposal,
        refetchType: `active`,
      },
      moduleInverterCompatibilityInvalidation(proposalId),
      {
        queryKey: queryKeys.legacy.graphql.proposal,
        refetchType: `active`,
      },
      { queryKey: queryKeys.lightReach.byProposal(proposalId) },
      // The save can add or remove the AI compute node the V1 slide reads.
      { queryKey: queryKeys.solarProposal.compute(proposalId) },
    ]),

  /**
   * A V2 designer save or reset: the design, its equipment and
   * impact show it, production may have moved, and pricing, savings and
   * contract readiness read out of date until someone refreshes them on
   * purpose.
   */
  solarDesignSaved: (
    queryClient: QueryClient,
    proposalId: string,
  ): Promise<void[]> =>
    invalidate(queryClient, [
      { queryKey: queryKeys.solarProposal.byId(proposalId), exact: true },
      { queryKey: queryKeys.solarProposal.design(proposalId), exact: true },
      // The edited version's row: its panels, size and production.
      {
        queryKey: queryKeys.solarProposal.designVersions(proposalId),
        exact: true,
      },
      { queryKey: queryKeys.solarProposal.designer(proposalId), exact: true },
      { queryKey: queryKeys.solarProposal.equipment(proposalId), exact: true },
      { queryKey: queryKeys.solarProposal.impact(proposalId), exact: true },
      { queryKey: queryKeys.solarProposal.savings(proposalId), exact: true },
      { queryKey: queryKeys.solarProposal.pricing(proposalId), exact: true },
      { queryKey: queryKeys.solarProposal.share(proposalId), exact: true },
      {
        queryKey: queryKeys.solarProposal.systemPricing(proposalId),
        exact: true,
      },
      {
        queryKey: queryKeys.solarProposal.productOffering(proposalId),
        exact: true,
      },
      { queryKey: queryKeys.solarProposal.contract(proposalId), exact: true },
      ...[
        ...solarDesignPricingInputSteps(proposalId),
        ...solarLightReachReaders(proposalId),
      ].map((queryKey) => ({
        queryKey,
        exact: true,
      })),
    ]),
} as const
